import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { GroupedFilterDrawer } from 'module/ui/components/molecules/field-filter/drawer/grouped-filter-drawer';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { TiptapJson } from 'api/page-template/types';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { DynamicSectionTable } from './table';
import { SectionData } from './table/types';
import {
  removeFilterConditionFromTiptapJson,
  tiptapJsonToSectionData,
  updateFilterConditionInTiptapJson,
} from './utils';

const FilterDrawerTitle = styled(CenterAlignedBox)``;

type FilterDrawerState = {
  filter: GroupedFilter;
  currentSection?: SectionData;
};

interface Props {
  fields: GroupedOptions<FilterableField>[];
  fieldsLookup: GroupFieldsMeta;
  tiptapJson: TiptapJson;
  onDocumentChange: (tiptapJson: TiptapJson) => void;
}

export const DynamicSection: FC<Props> = ({
  fields,
  tiptapJson,
  onDocumentChange,
  fieldsLookup,
}) => {
  const gridApi = useRef<GridApi | undefined>(undefined);
  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApi.current = event.api;
  }, []);
  const filterTransformer = useMemo(
    () => getApiGroupedFilterTransformer(fieldsLookup, FilterGroupType.User),
    [fieldsLookup],
  );
  const [filterDrawerState, setFilterDrawerState] = useState<FilterDrawerState>(
    {
      filter: defaultGroupedFilter(FilterGroupType.User),
    },
  );
  const editFilter = useCallback((section: SectionData) => {
    setFilterDrawerState({
      filter: section.filter ?? defaultGroupedFilter(FilterGroupType.User),
      currentSection: section,
    });
  }, []);
  const removeFilter = useCallback(
    (section: SectionData) => {
      if (gridApi.current == null) {
        return;
      }
      const updatedTiptapJson = removeFilterConditionFromTiptapJson(
        tiptapJson,
        section,
      );

      onDocumentChange(updatedTiptapJson);
    },
    [tiptapJson, onDocumentChange],
  );
  const closeFilterDrawer = useCallback(() => {
    setFilterDrawerState({
      filter: defaultGroupedFilter(FilterGroupType.User),
    });
  }, []);
  const onDone = useCallback(
    (filter: GroupedFilter) => {
      if (filterDrawerState.currentSection == null) {
        return;
      }
      const updatedTiptapJson = updateFilterConditionInTiptapJson(
        tiptapJson,
        filterDrawerState.currentSection,
        filterTransformer.to(filter),
      );

      onDocumentChange(updatedTiptapJson);
      closeFilterDrawer();
    },
    [
      filterDrawerState.currentSection,
      tiptapJson,
      filterTransformer,
      onDocumentChange,
      closeFilterDrawer,
    ],
  );

  const rowData = useMemo(() => {
    return tiptapJsonToSectionData(tiptapJson, filterTransformer);
  }, [tiptapJson, filterTransformer]);

  return (
    <>
      <DynamicSectionTable
        onEditFilter={editFilter}
        onDeleteFilter={removeFilter}
        gridApi={gridApi.current}
        onGridReady={onGridReady}
        sectionData={rowData}
      />
      <GroupedFilterDrawer
        title={
          <FilterDrawerTitle>
            <FontAwesomeIcon icon={faFilter} />
            Add dynamic conditions
          </FilterDrawerTitle>
        }
        hasAiQuery={true}
        hideRemoveButton={true}
        fields={fields}
        filter={filterDrawerState.filter}
        onChange={onDone}
        visible={filterDrawerState.currentSection != null}
        onClose={closeFilterDrawer}
      />
    </>
  );
};
